import { useState, useRef, useEffect } from 'react';
import { MoreHorizontal, Eye, Download, FileCheck, Trash2, Copy, Send } from 'lucide-react';

const MENU_HEIGHT_ESTIMATE = 220;
const MENU_WIDTH = 224;

export default function EnvelopeActions({ envelope, onAction }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null); // { top, left } in viewport px
  const ref = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    }
    function handleScroll() { setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  // Compute fixed-position coordinates from the trigger's bounding rect so
  // the menu escapes any ancestor overflow clipping (the table wrapper and
  // card both clip), and flips upward when space below is tight.
  useEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < MENU_HEIGHT_ESTIMATE;
    setMenuPos({
      top: openUpward ? rect.top - 4 : rect.bottom + 4,
      left: Math.max(8, rect.right - MENU_WIDTH),
      upward: openUpward,
    });
  }, [open]);

  const isDraft = envelope.status === 'draft';
  const isCompleted = envelope.status === 'completed';
  const isSent = envelope.status === 'sent';

  const items = [];
  items.push({ key: 'view', label: 'View', Icon: Eye });
  if (isCompleted) {
    items.push({ key: 'download-signed', label: 'Download signed PDF', Icon: FileCheck });
    items.push({ key: 'download-cert', label: 'Download certificate', Icon: Download });
  }
  if (isSent) {
    items.push({ key: 'resend-all', label: 'Resend to pending', Icon: Send });
  }
  if (!isDraft) {
    items.push({ key: 'duplicate', label: 'Duplicate', Icon: Copy });
  }
  if (isSent) {
    items.push({ key: 'void', label: 'Void', Icon: Trash2, danger: true });
  }
  if (isDraft) {
    items.push({ key: 'delete', label: 'Delete draft', Icon: Trash2, danger: true });
  }

  return (
    <>
      <div ref={ref}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
          aria-label="Actions"
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </div>

      {open && menuPos && (
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPos.upward ? undefined : menuPos.top,
            bottom: menuPos.upward ? window.innerHeight - menuPos.top : undefined,
            left: menuPos.left,
            width: MENU_WIDTH,
            zIndex: 50,
          }}
          className="bg-white border border-gray-200 rounded-lg shadow-lg py-1"
        >
          {items.map((item) => (
            <button
              key={item.key}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onAction(item.key);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${
                item.danger ? 'text-red-600' : 'text-gray-700'
              }`}
            >
              <item.Icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
